import { createSelector, createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { ApolloClient, useApolloClient } from "@apollo/client";
import {
  GET_HISTORY_BY_ID,
  GET_HISTORY_BY_SHARE_ID,
  GET_IMAGE_COLOR_INFERENCE,
  GET_INFERENCE_BY_ID,
  UPDATE_INSPECT_RESULT,
} from "apollo/queries";
import { DEFAULT_COLOR_DESCRIPTION_RICE_RESULT, defectRiceLevel } from "utility/constants";
import { checkKeyPresenceInArray, isNotEmpty } from "utility";
import {
  CreateInspectResultDocument,
  GetEvoDataDocument,
  GetHistoryByIdDocument,
  GetInferenceDocument,
  GetShareHistoryDocument,
  ImageColorInferenceDocument,
  ImageColorInferenceQuery,
  InputDataRiceWithoutFormValue,
  InputResultData,
  OutputImageColor,
  OutputResult,
  OutputResultData,
  UpdateInspectResultDocument,
  UpdateInspectResultMutationVariables,
  UserAttribute,
} from "__generated__/graphql";
import { RootState } from "redux/store";
import { SETTING_RICE_LEVEL } from "sections/setting/inspect/config";

// ----------------------------------------------------------------------
type ModeResult = "create" | "history" | "share";

type DataInfoOriginalValue = {
  historyId?: string;
  formId: string;
  standardId: string;
  riceId?: string;
  formName?: string;
  standardName?: string;
  riceName?: string;
  datetime?: string;
  isBeta?: boolean;
  inferenceId: string[];
  createBy?: string;
  createByProfle?: UserAttribute;
};
type DataInfoSetting = {
  chalkyLevel: number;
  headLevel: number;
  yellowLevel: number;
  undermilledLevel: number;
  damagedLevel: number;
  isAutoYellow: boolean;
  averageLength?: number;
};
type DataInfoValue = {
  // resultData
  sumWeight: number;
  totalGrain: number;
  isPass: boolean;
  createResultAt?: string;
} & DataInfoOriginalValue &
  DataInfoSetting;

// export type DataFormValue = {
//   lot?: string;
//   note?: string;
//   regis?: string;
//   sellerName?: string;
//   buyerName?: string;
//   // [x: string]: any;
// };

type ProcessFormValue = {
  key: string;
  name: string;
  color: string;
  dataForm: DataFormValue[];
};

export type OptionInputValue = {
  type:
    | "string"
    | "number"
    | "date"
    | "time"
    | "datetime"
    | "dropdown"
    | "unit"
    | "checkbox"
    | (string & {});
  length: number;
  min: number;
  max: number;
  decimal: number;
  option: string[];
};
export type DataFormValue = {
  key: string;
  name: string;
  value?: string;
  values?: string[];
  valueUnit: string;
  optionInput: OptionInputValue;
};

export type DataResultByValue = {
  condition: string;
  condition_max: string;
  condition_min: string;
  grainRequire: string;
  grainRequire_max: string;
  grainRequire_min: string;
  id: string;
  input: string;
  isGrainPass: boolean;
  isInput: boolean;
  key: string;
  maxLength: string;
  minLength: string;
  name: string;
  percentage: number;
  typeUnit: string;
  unit: string;
  unitID: string;
  value: string;
  optionInput: {
    type: "string" | "number";
    length: number;
    min: number;
    max: number;
    decimal: number;
  };
};
export type DataResultValue = {
  name: string;
  value: DataResultByValue[];
  column: {
    detail: boolean;
    standard: boolean;
    note: boolean;
  };
  split: boolean;
};

export type ImageValue = {
  inferenceID: string;
  src: string;
  descriptionColorType: {
    shape: string;
    type: string;
  };
  imageShape: string;
  imageType: string;
};

export type IRiceInspectResultState = {
  error?: any;
  isLoading?: boolean;
  isFoundData?: boolean;
  mode?: ModeResult;
  historyId?: string;
  shareId?: string;
  isEdit?: boolean;
  image?: ImageValue;
  dataInfo?: DataInfoValue;
  dataResult?: DataResultValue[];
  rawData?: OutputResultData;
  // form
  process?: ProcessFormValue;
};

const initialState: IRiceInspectResultState = {
  error: null,
  isLoading: false,
  isFoundData: false,
  mode: "create",
  isEdit: false,
  image: undefined,
  dataResult: [],
};

const slice = createSlice({
  name: "riceInspectResult",
  initialState,
  reducers: {
    reset: () => initialState,
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearError(state) {
      state.error = null;
    },
    openEdit(state) {
      state.isEdit = true;
    },
    closeEdit(state) {
      state.isEdit = false;
    },
    selectMode(state, action) {
      const { mode, id } = action.payload;
      state.mode = mode;
      if (mode === "create") {
        state.isEdit = true;
      }
      if (mode === "history") {
        state.historyId = id;
        state.isEdit = false;
      }
      if (mode === "share") {
        state.shareId = id;
        state.isEdit = false;
      }
    },
    getInspectResultSuccess(state, action: PayloadAction<IRiceInspectResultState>) {
      const { image, dataInfo, dataResult, rawData, process } = action.payload;
      state.isFoundData = true;
      state.image = image;
      state.dataInfo = { ...state.dataInfo, ...dataInfo };
      state.process = { ...state.process, ...process };
      state.dataResult = dataResult;
      state.rawData = rawData;
      state.isLoading = false;
    },
    createInspectResultSuccess(state, action: PayloadAction<IRiceInspectResultState>) {
      const { dataInfo, dataResult, process, historyId } = action.payload;
      state.isFoundData = true;
      state.dataInfo = { ...state.dataInfo, ...dataInfo };
      state.process = { ...state.process, ...process };
      state.dataResult = dataResult;
      state.historyId = historyId;
      state.isLoading = false;
      state.isEdit = false;
    },
    getHistorySuccess(state, action: PayloadAction<IRiceInspectResultState>) {
      const { image, dataInfo, dataResult, rawData, process } = action.payload;
      state.isFoundData = true;
      state.image = image;
      state.dataInfo = dataInfo;
      state.process = process;
      state.dataResult = dataResult;
      state.rawData = rawData;
      state.isLoading = false;
    },
    updateHistorySuccess(state, action: PayloadAction<IRiceInspectResultState>) {
      const { dataInfo, process, dataResult } = action.payload;
      state.isFoundData = true;
      state.dataInfo = { ...state.dataInfo, ...dataInfo };
      state.process = { ...state.process, ...process };
      state.dataResult = dataResult;
      state.isLoading = false;
      state.isEdit = false;
    },
  },
});

// Reducer
export default slice.reducer;

export function resetRiceInspectResult() {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.reset());
  };
}

export function openEdit() {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.openEdit());
  };
}

export function updateMode(mode: ModeResult, id?: string) {
  return async (dispatch: Dispatch) => {
    dispatch(
      slice.actions.selectMode({
        mode: mode,
        id,
      })
    );
  };
}

export function updateHistory(
  client: ApolloClient<object>,
  data: UpdateInspectResultMutationVariables
) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(slice.actions.clearError());
      const { data: dataUpdate } = await client.mutate({
        mutation: UpdateInspectResultDocument,
        variables: data,
        fetchPolicy: "network-only",
      });
      const result = handleSetValue(dataUpdate?.updateInspectResult);
      dispatch(
        slice.actions.updateHistorySuccess({
          dataInfo: {
            isPass: result.dataInfo.isPass,
          },
          process: {
            dataForm: JSON.parse(data.processFormValue),
          },
          dataResult: result.dataResult,
        })
      );
    } catch (err) {
      console.error(err);
      dispatch(slice.actions.hasError(err));
      throw err;
    }
  };
}

const handleGetImage = async (client: ApolloClient<object>, inferenceId: string[], standardId) => {
  const imageColorInference = await client.query({
    query: ImageColorInferenceDocument,
    variables: {
      setOfImageKey: inferenceId.map((e) => ({
        inferenceID: e,
        standardID: standardId,
      })),
    },
    fetchPolicy: "network-only",
  });
  return {
    imageColorInference,
  };
};

type GetInspectResultProps = {
  client: ApolloClient<object>;
  dataInfo: DataInfoOriginalValue;
  process?: ProcessFormValue;
  isAllowAverageLength?: boolean;
  setting: DataInfoSetting;
};
export function getWhitenessResult({ process, client, dataInfo }) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      dispatch(
        slice.actions.selectMode({
          mode: "create",
        })
      );
      const { data } = await client.query({
        query: GetEvoDataDocument,
        variables: {
          formID: dataInfo.formId,
          standardID: dataInfo.standardId,
          inferenceID: dataInfo.inferenceId,
        },
        fetchPolicy: "network-only",
      });
      const { imageColorInference } = await handleGetImage(
        client,
        dataInfo.inferenceId,
        dataInfo.standardId
      );
      const result = data.getEvoData as OutputResultData;
      dispatch(
        slice.actions.getInspectResultSuccess({
          image: imageColorInference?.data?.imageColorInference.map(mapKeyImageColor)[0],
          dataInfo: {
            ...dataInfo,
            isPass: result.isPass,
            sumWeight: result.sumWeight,
            totalGrain: result.totalGrain,
            chalkyLevel: result.chalkyLevel,
            damagedLevel: result.damagedLevel,
            headLevel: result.headLevel,
            undermilledLevel: result.undermilledLevel,
            yellowLevel: result.yellowLevel,
            isAutoYellow: result.isAutoYellow,
            createResultAt: result.createResultAt,
          } as DataInfoValue,
          process: process,
          dataResult: result.data,
          rawData: result,
        })
      );
    } catch (err) {
      console.log(err);
      dispatch(slice.actions.hasError(err));
      throw err;
    }
  };
}

export function getInspectResult({
  client,
  dataInfo,
  process,
  isAllowAverageLength,
  setting,
}: GetInspectResultProps) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      dispatch(
        slice.actions.selectMode({
          mode: "create",
        })
      );
      const { data } = await client.query({
        query: GetEvoDataDocument,
        variables: {
          formID: dataInfo.formId,
          standardID: dataInfo.standardId,
          inferenceID: dataInfo.inferenceId,
          yellowLevel: setting.yellowLevel?.toString(),
          isAutoYellow: setting.isAutoYellow,
          ...(isAllowAverageLength && {
            averageLength: setting.averageLength,
          }),
        },
        fetchPolicy: "network-only",
      });
      const { imageColorInference } = await handleGetImage(
        client,
        dataInfo.inferenceId,
        dataInfo.standardId
      );
      const result = data.getEvoData as OutputResultData;

      dispatch(
        slice.actions.getInspectResultSuccess({
          image: imageColorInference?.data?.imageColorInference.map(mapKeyImageColor)[0],
          dataInfo: {
            ...dataInfo,
            isPass: result.isPass,
            sumWeight: result.sumWeight,
            totalGrain: result.totalGrain,
            chalkyLevel: result.chalkyLevel,
            damagedLevel: result.damagedLevel,
            headLevel: result.headLevel,
            undermilledLevel: result.undermilledLevel,
            yellowLevel: result.yellowLevel,
            isAutoYellow: result.isAutoYellow,
            createResultAt: result.createResultAt,
          } as DataInfoValue,
          process: process,
          dataResult: result.data,
          rawData: result,
        })
      );
    } catch (err) {
      console.log(err);
      dispatch(slice.actions.hasError(err));
      throw err;
    }
  };
}

export type CreateHistoryProps = {
  dataRice: InputDataRiceWithoutFormValue;
  inferenceID?: string | string[];
  resultData: InputResultData;
  weight?: number;
  process: ProcessFormValue;
};
export function createHistory(client: ApolloClient<object>, data: CreateHistoryProps) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(slice.actions.clearError());
      const { data: dataCreate } = await client.mutate({
        mutation: CreateInspectResultDocument,
        variables: {
          ...data,
          processFormID: data.process.key,
          processFormValue: JSON.stringify(data.process.dataForm),
        },
        fetchPolicy: "network-only",
      });
      const result = handleSetValue(dataCreate?.createInspectResult);
      dispatch(
        slice.actions.createInspectResultSuccess({
          dataInfo: {
            historyId: result.dataInfo.historyId,
            datetime: result.dataInfo.datetime,
            isPass: result.dataInfo.isPass,
            isBeta: result.dataInfo.isBeta,
          },
          process: data.process,
          dataResult: result.dataResult,
          historyId: result.dataInfo.historyId,
        })
      );
      return result.dataInfo.historyId;
    } catch (err) {
      console.error(err);
      dispatch(slice.actions.hasError(err));
      throw err;
    }
  };
}

export function createWhitenessHistory(client: ApolloClient<object>, data: CreateHistoryProps) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(slice.actions.clearError());
      const { data: dataCreate } = await client.mutate({
        mutation: CreateInspectResultDocument,
        variables: {
          ...data,
          processFormID: data?.process?.key,
          processFormValue: JSON.stringify(data?.process?.dataForm),
        },
        fetchPolicy: "network-only",
      });
      const result = handleSetValue(dataCreate?.createInspectResult);
      dispatch(
        slice.actions.createInspectResultSuccess({
          dataInfo: {
            historyId: result?.dataInfo?.historyId,
            datetime: result?.dataInfo?.datetime,
            isPass: result?.dataInfo?.isPass,
            isBeta: result?.dataInfo?.isBeta,
          },
          process: data?.process,
          dataResult: result?.dataResult,
          historyId: result?.dataInfo.historyId,
        })
      );
      return result.dataInfo.historyId;
    } catch (err) {
      console.error(err);
      dispatch(slice.actions.hasError(err));
      throw err;
    }
  };
}
export function getHistoryById(client: ApolloClient<object>, id: string) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      dispatch(
        slice.actions.selectMode({
          mode: "history",
          id: id,
        })
      );
      const { data } = await client.query({
        query: GetHistoryByIdDocument,
        variables: {
          historyID: id,
        },
        fetchPolicy: "network-only",
      });

      if (!data?.getHistoryById?.resultData) throw "NotFoundData";
      const dataHistory = data?.getHistoryById;
      const inferenceID = dataHistory?.inferenceID;
      const { imageColorInference } = await handleGetImage(
        client,
        inferenceID,
        dataHistory?.standardID
      );
      dispatch(
        slice.actions.getHistorySuccess({
          image: imageColorInference?.data?.imageColorInference.map(mapKeyImageColor)[0],
          ...handleSetValue(dataHistory),
          rawData: dataHistory.resultData,
          ฟหกฟ: "",
        })
      );
    } catch (err) {
      console.log(err);
      dispatch(slice.actions.hasError(err));
    }
  };
}

export function getHistoryByShareId(client: ApolloClient<object>, id: string) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      dispatch(
        slice.actions.selectMode({
          mode: "share",
          id: id,
        })
      );
      const { data } = await client.query({
        query: GetShareHistoryDocument,
        variables: {
          shareID: id,
        },
        fetchPolicy: "network-only",
      });

      if (!data) throw "NotFoundData";
      const dataHistory = data?.getShareHistory;
      const inferenceID = dataHistory?.inferenceID;
      const { imageColorInference } = await handleGetImage(
        client,
        inferenceID,
        dataHistory?.standardID
      );
      dispatch(
        slice.actions.getHistorySuccess({
          image: imageColorInference?.data?.imageColorInference.map(mapKeyImageColor)[0],
          ...handleSetValue(dataHistory),
          rawData: dataHistory.resultData,
        })
      );
    } catch (err) {
      console.log(err);
      dispatch(slice.actions.hasError(err));
    }
  };
}

// util
const handleSetValue = (data: OutputResult) => {
  return {
    dataInfo: {
      historyId: data.historyID,
      formId: data.formID,
      standardId: data.standardID,
      riceId: data.riceID,
      formName: data.formName,
      standardName: data.standardName,
      riceName: data.riceTypeName,
      datetime: data.myDate,
      isBeta: data.isBeta,
      isPass: data.resultData.isPass,
      inferenceId: data.inferenceID,
      sumWeight: data.resultData.sumWeight,
      totalGrain: data.resultData.totalGrain,
      chalkyLevel: data.resultData.chalkyLevel,
      damagedLevel: data.resultData.damagedLevel,
      headLevel: data.resultData.headLevel,
      undermilledLevel: data.resultData.undermilledLevel,
      yellowLevel: data.resultData.yellowLevel,
      isAutoYellow: data.resultData.isAutoYellow,
      createResultAt: data.resultData.createResultAt,
      createBy: data?.createBy,
      createByProfle: data?.createByProfle,
    } as DataInfoValue,
    ...(data?.process && {
      process: data.process,
    }),
    dataResult: data.resultData.data,
  };
};

const mapKeyImageColor = (v: OutputImageColor) => ({
  inferenceID: v.inferenceID,
  imageShape: `${v.imageUrls.shape}`,
  imageType: `${v.imageUrls.type}`,
  descriptionColorType: {
    shape: v.imageColorSet?.shape || DEFAULT_COLOR_DESCRIPTION_RICE_RESULT.shape,
    type: v.imageColorSet?.type || DEFAULT_COLOR_DESCRIPTION_RICE_RESULT.type,
  },
  src: v?.imageUrls?.source,
});

// select
export const selectResultLevelSetting = (state: RootState) => {
  return {
    chalkyLevel: state.riceInspectResult.dataInfo.chalkyLevel,
    headLevel: SETTING_RICE_LEVEL.head.convert.toValue(state.riceInspectResult.dataInfo.headLevel),
    yellowLevel: state.riceInspectResult.dataInfo.yellowLevel,
    undermilledLevel: state.riceInspectResult.dataInfo.undermilledLevel,
    damagedLevel: state.riceInspectResult.dataInfo.damagedLevel,
    isAutoYellow: state.riceInspectResult.dataInfo.isAutoYellow,
  };
};

export const selectResultInspectTemplate = (state: IRiceInspectResultState) => {
  return state.dataResult.map((e) => ({
    ...e,
    value: e.value.map((v) => ({
      ...v,
      value: isNotEmpty(v.percentage) ? v.percentage : v.value,
    })),
  }));
};

export const selectCreateResultAt = (state: RootState) => {
  return state.riceInspectResult?.dataInfo?.createResultAt;
};
